'use client';

import React, { useState } from 'react';
import { Box, Button, Dialog, Input } from 'src/components';
import { useUpdateAdvancedSearchMutation } from 'src/api/redux/advancedSearches';
import { useNotification } from 'src/context/notification';

interface RenameAISearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  volumeToRename: {
    id: number;
    label: string;
  };
  onRenameSuccess?: (id: number, newLabel: string) => void;
}

const RenameAISearchDialog: React.FC<RenameAISearchDialogProps> = ({
  isOpen,
  onClose,
  volumeToRename,
  onRenameSuccess,
}) => {
  const { setNotification } = useNotification();
  const [updateAdvancedSearch, { isLoading: isSaving }] =
    useUpdateAdvancedSearchMutation();
  const [newName, setNewName] = useState(volumeToRename.label ?? '');

  const trimmedName = newName.trim();
  const isSaveDisabled =
    isSaving || !trimmedName || trimmedName === volumeToRename.label;

  const handleSave = async () => {
    if (isSaveDisabled) return;

    try {
      await updateAdvancedSearch({
        id: volumeToRename.id,
        data: { label: trimmedName },
      }).unwrap();

      setNotification({
        title: 'Success',
        message: `AI search renamed to "${trimmedName}"`,
        type: 'success',
      });

      onRenameSuccess?.(volumeToRename.id, trimmedName);
      onClose();
    } catch (error: any) {
      setNotification({
        title: 'Error',
        message:
          error?.data?.error_message ||
          'Failed to rename AI search. Please try again.',
        type: 'error',
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="rename-ai-search-dialog-title"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <form onSubmit={handleSubmit} className="p-6">
          <h2
            id="rename-ai-search-dialog-title"
            data-testid="rename-ai-search-dialog-title"
            className="text-base text-neutral-800 font-semibold mb-4"
          >
            Rename AI Search
          </h2>
          <Input
            data-testid="rename-ai-search-dialog-input"
            variant="outlined"
            size="md"
            fullWidth
            placeholder="Type a name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
            aria-label="AI search name"
          />
          <Box className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="px-4"
              data-testid="rename-ai-search-dialog-cancel-button"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="solid"
              color="primary"
              className="px-4"
              data-testid="rename-ai-search-dialog-save-button"
              loading={isSaving}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </Box>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};

export default RenameAISearchDialog;
